/**
 * Utility functions for language detection and handling
 */
import React from 'react';

/**
 * Detects if a text contains Hebrew characters and returns the language direction
 * @param {string} text - The text to analyze
 * @returns {string} - 'rtl' for Hebrew content, 'ltr' for others
 */
export const detectLanguageDirection = (text) => {
  if (!text || typeof text !== 'string') return 'ltr';
  // Find the first strong directional character (Hebrew or English)
  // Hebrew range: \u0590-\u05FF
  // English range: a-zA-Z
  const match = text.match(/[\u0590-\u05FFa-zA-Z]/);

  // If the first strong character is Hebrew, return 'rtl'
  if (match && /[\u0590-\u05FF]/.test(match[0])) {
    return 'rtl';
  }

  // Default to 'ltr' for English or mixed content starting with English
  return 'ltr';
};

/**
 * Detects if a text contains Hebrew characters and returns the language code
 * @param {string} text - The text to analyze
 * @returns {string} - 'he' for Hebrew content, 'en' for others
 */
export const detectLanguage = (text) => {
  if (!text || typeof text !== 'string') return 'en';
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text) ? 'he' : 'en';
};

/**
 * Splits text with mixed language content and wraps English parts in appropriate spans
 * @param {string} text - The mixed content text
 * @param {boolean} isHeading - Whether this is a heading element
 * @returns {(string|JSX.Element)[]} - Array of text segments and wrapped spans
 */
export const splitTextAndWrap = (text, isHeading = false) => {
  if (typeof text !== 'string') return text;

  // If it's an English heading, return as is
  if (isHeading && detectLanguage(text) === 'en') {
    return text;
  }

  // Only for Hebrew or mixed text – תואמים אנגלית בלי לספח מקף/סימנים בסוף (כדי ש־Bidi לא ישבש "MIT-שלהן")
  // מילים באנגלית (כולל עם מקף פנימי כמו "middle-of-the-road"), לא מקף בסוף
  const regex = /(\([^)]+\)|[a-zA-Z]+(?:-[a-zA-Z]+)*(?:\s+[a-zA-Z]+(?:-[a-zA-Z]+)*)*)/g;
  let lastIndex = 0;
  const parts = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(match[0]);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.map((part, index) => {
    const isEnglish = /^[a-zA-Z\s()]+$/.test(part) || /^[a-zA-Z]+(?:-[a-zA-Z]+)*(?:\s+[a-zA-Z]+(?:-[a-zA-Z]+)*)*$/.test(part);
    return isEnglish ? (
      <span
        key={index}
        dir="ltr"
        style={{
          unicodeBidi: 'isolate'
        }}
      >
        {part}
      </span>
    ) : part;
  });
};

/**
 * Wraps content with appropriate language styling based on parent language
 * @param {string|React.ReactNode} children - The content to wrap
 * @param {string} parentLang - Parent language ('en' or 'he')
 * @param {boolean} isHeading - Whether this is a heading element
 * @returns {JSX.Element} - Wrapped content with appropriate direction
 */
/** ממיר children למערך ומעבד כל מחרוזת ל־Bidi; שומר אלמנטים-is */
function processChildrenBidi(children, parentLang, isHeading) {
  const arr = Array.isArray(children) ? children : [children];
  const processed = arr.flatMap((child) => {
    if (typeof child === 'string') return splitTextAndWrap(child, isHeading);
    if (React.isValidElement(child) && child.props?.children != null) {
      const inner = processChildrenBidi(React.Children.toArray(child.props.children), parentLang, isHeading);
      return [React.cloneElement(child, { key: child.key }, inner)];
    }
    return [child];
  });
  return processed;
}

export const wrapWithLanguage = (children, parentLang, isHeading = false) => {
  const isString = typeof children === 'string';
  if (!isString && !Array.isArray(children) && !React.isValidElement(children)) return children;

  // כשהתוכן מעורב (מערך/אלמנטים) – מעבדים כל מחרוזת ל־Bidi
  if (!isString) {
    const processed = processChildrenBidi(Array.isArray(children) ? children : [children], parentLang, isHeading);
    return (
      <span style={{
        display: 'block',
        direction: parentLang === 'en' ? 'ltr' : 'rtl',
        textAlign: parentLang === 'en' ? 'left' : 'right'
      }}>
        {processed}
      </span>
    );
  }

  // Special handling for English headings
  if (isHeading && parentLang === 'en') {
    return (
      <span style={{
        display: 'block',
        direction: 'ltr',
        textAlign: 'left',
        width: '100%'
      }}>
        {children}
      </span>
    );
  }

  // Handle regular text
  return (
    <span style={{
      display: 'block',
      direction: parentLang === 'en' ? 'ltr' : 'rtl',
      textAlign: parentLang === 'en' ? 'left' : 'right'
    }}>
      {splitTextAndWrap(children, isHeading)}
    </span>
  );
};
